import React, { useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';
import { MAIN_THEME } from '@styles';
import { isEmpty, isValidNumber } from '@utils/validationUtils';
import { IFormFieldInputBaseProps } from '.';
import FormFieldWrapper from './FormFieldWrapper';
import { trimTrailingZeros } from '@utils/formatting-utils';

export interface INumberInputFormFieldProps
  extends IFormFieldInputBaseProps {
  value: number | string;
  loader?: boolean;
  hideError?: boolean;
  maxValue?: number;
  minValue?: number;
  allowNegativeValues?: boolean;
  allowExponential?: boolean;
  maxDecimalPrecision?: number;
  formatOnBlur?: boolean;
  classes?: {
    labelWrapper?: string;
    label?: string;
    input?: string;
  };
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const useStyles = makeStyles()(() => ({
  input: {
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '15px',
    height: 35,
    width: '100%',
    padding: '0 15px',
    margin: '0 auto',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white86}`,
    boxSizing: 'border-box',
    borderRadius: '5px',
    '&:disabled': {
      cursor: 'not-allowed',
      color: 'rgba(0, 0, 0, 0.38)!important',
    },
    '&::placeholder': {
      fontStyle: 'italic',
      fontFamily: MAIN_THEME.font.regular,
      color: MAIN_THEME.color.white86,
      opacity: 1,
      fontSize: MAIN_THEME.fontSize.sm,
    },
    fontFamily: MAIN_THEME.font.regular,
    '&:focus': {
      outline: 'none',
    },
    '::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  },
  loader: {
    position: 'absolute',
    top: 'calc(50% - 10px)',
    right: '3%',
    opacity: 1,
    height: '25px',
    width: '25px',
  },
  materialInput: {
    backgroundColor: 'unset',
    border: 'none',
    borderBottom: `1px solid ${MAIN_THEME.color.white86}`,
    color: MAIN_THEME.color.black,
    margin: '0 auto',
    borderRadius: 0,
    padding: '10px 15px 4px 4px',
    fontSize: `${MAIN_THEME.fontSize.sm}`,
    MozAppearance: 'textfield',
  },
  inputError: {
    borderColor: `${MAIN_THEME.color.red} !important`,
    outlineColor: MAIN_THEME.color.red,
  },
  inputContainer: {
    position: 'relative',
  },
  disabledInput: {
    '& .MuiInputBase-root.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.5)!important',
      WebkitTextFillColor: 'rgba(0, 0, 0, 0.5)!important',
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.5)!important',
      WebkitTextFillColor: 'rgba(0, 0, 0, 0.5)!important',
    },
  },
}));

const NumberInputFormField: React.FC<INumberInputFormFieldProps> = ({
  error,
  name,
  title,
  value = '',
  placeholder,
  disabled,
  material,
  loader,
  inputOnly,
  hideError,
  classes = {},
  maxValue,
  minValue,
  allowNegativeValues = false,
  allowExponential = false,
  formatOnBlur = true,
  maxDecimalPrecision = 3,
  onChange,
  onFocus,
  onBlur,
}) => {
  const { classes: inputClasses } = useStyles();
  const hasError = !!error;
  const errorId = `errorMsg-${name}`;

  const limitNumber = useCallback(
    (targetValue: string) => {
      if (
        Number(maxDecimalPrecision) >= 0 &&
        isValidNumber(maxDecimalPrecision)
      ) {
        targetValue = trimTrailingZeros(
          targetValue,
          maxDecimalPrecision
        ).toString();
      }
      return targetValue;
    },
    [maxDecimalPrecision]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = event.currentTarget.value;

      // If the user cleared the field, allow empty string
      if (newValue === '') {
        onChange({
          ...event,
          target: {
            ...event.target,
            value: '',
            name: event.target.name,
          },
        });
        return;
      }

      // When maxDecimalPrecision is 0, prevent input of decimal point
      if (maxDecimalPrecision === 0 && newValue.includes('.')) {
        return;
      }

      const pattern = allowExponential
        ? allowNegativeValues
          ? /^-?(\d+(\.\d*)?)([eE][+-]?\d+)?$/
          : /^(\d+(\.\d*)?)([eE][+-]?\d+)?$/
        : allowNegativeValues
          ? /^-?\d*\.?\d*$/
          : /^\d*\.?\d*$/;

      if (!pattern.test(newValue)) {
        return;
      }

      // If the user just typed "-" or "-.", allow them to keep typing
      if (newValue === '-' || newValue === '-.') {
        onChange({
          ...event,
          target: {
            ...event.target,
            value: newValue,
            name: event.target.name,
          },
        });
        return;
      }

      if (isNaN(parseFloat(newValue))) {
        newValue = '';
      } else {
        if (newValue.includes('.')) {
          const [integerPart, fractionalPart] = newValue.split('.');
          if (fractionalPart.length > maxDecimalPrecision) {
            newValue = `${integerPart}.${fractionalPart.substring(0, maxDecimalPrecision)}`;
          }
        }
      }

      if (isValidNumber(minValue) && parseFloat(newValue) < minValue) {
        newValue = minValue.toString();
      } else if (isValidNumber(maxValue) && parseFloat(newValue) > maxValue) {
        newValue = maxValue.toString();
      }
      onChange({
        ...event,
        target: {
          ...event.target,
          value: newValue,
          name: event.target.name,
        },
      });
    },
    [
      minValue,
      maxValue,
      onChange,
      maxDecimalPrecision,
      allowNegativeValues,
      allowExponential,
    ]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (formatOnBlur && !isEmpty(event.target.value)) {
        const targetValue = event.target.value;
        const newValue = limitNumber(targetValue);
        onChange({
          ...event,
          target: {
            ...event.target,
            value: newValue,
          },
        });
      }
      onBlur && onBlur(event);
    },
    [onBlur, limitNumber, onChange, formatOnBlur]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // If exponential is not allowed, prevent entering 'e' or 'E'.
      if (!allowExponential && (event.key === 'e' || event.key === 'E')) {
        event.preventDefault();
      }
    },
    [allowExponential]
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      // prevent scrolling on input focus
      event.target.addEventListener(
        'wheel',
        function (e) {
          e.preventDefault();
        },
        { passive: false }
      );

      onFocus && onFocus(event);
    },
    [onFocus]
  );

  const renderInput = () => (
    <div className={inputClasses.inputContainer}>
      <input
        className={clsx(inputClasses.input, {
          [inputClasses.materialInput]: material,
          [inputClasses.inputError]: error,
          [inputClasses.disabledInput]: disabled,
          [classes.input]: classes.input,
        })}
        title={disabled && !isEmpty(value) ? value.toString() : title}
        id={name}
        min={allowNegativeValues ? minValue : 0}
        max={maxValue}
        name={name}
        inputMode="numeric"
        aria-invalid={hasError}
        aria-describedby={hasError ? errorId : undefined}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        type={'text'}
        disabled={disabled}
        placeholder={placeholder}
      />
    </div>
  );

  return inputOnly ? (
    renderInput()
  ) : (
    <FormFieldWrapper
      title={title}
      error={error}
      name={name}
      material={material}
      className={classes?.labelWrapper}
      hideError={hideError}
    >
      {renderInput()}
      {loader && (
        <CircularProgress
          className={inputClasses.loader}
          size={20}
          thickness={3}
        />
      )}
    </FormFieldWrapper>
  );
};

export default NumberInputFormField;
